import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useLanguage } from '@/contexts/LanguageContext';
import { Clinic, getClinic } from '@/data/Clinics';
import { DentistHour, getDentistHoursByDentistId } from '@/data/DentistHours';
import { Dentist, getDentistsByClinicId } from '@/data/Dentists';
import {
  Patient,
  createPatient,
  getPatient,
  updatePatient
} from '@/data/Patients';
import {
  Request,
  createRequest,
  getDentistFutureAppointmentsAndRequests
} from '@/data/Requests';
import {
  Service,
  getDefaultServices,
  getServicesByDentistId
} from '@/data/Services';
import useToast from '@/hooks/useToast';
import {
  DEFAULT_DURATION_IN_MINUTES,
  MAXIMUM_DATE_RANGE_IN_MONTHS,
  MAXIMUM_DURATION_IN_MINUTES,
  OVERLAP_TIME_REDUCTION_IN_MINUTES,
  PURPOSE_JOIN_SEPARATOR,
  REQUEST_STATUS_OPTIONS,
  SCHEDULE_FLOW_STEPS,
  SELECT_MODAL_OTHERS,
  TIMESLOT_BUFFER_IN_HOURS
} from '@/utils/constants';
import { formatDentistName } from '@/utils/helpers/formatDentistName';
import { formatMobileNumber } from '@/utils/helpers/formatMobileNumber';
import { formatName } from '@/utils/helpers/formatName';
import { generateUid, isValidUid } from '@/utils/helpers/uid';
import { supabase } from '@/utils/supabase';
import { Gender } from '@/utils/types';
import { UseQueryResult, useMutation, useQuery } from '@tanstack/react-query';
import moment from 'moment';
import { usePostHog } from 'posthog-js/react';
import { useParams } from 'react-router';

interface ScheduleFlowContextProps {
  loading: boolean;
  clinic: Clinic | null;
  dentists: Dentist[];
  selectedDentist: Dentist | null;
  setSelectedDentist: React.Dispatch<React.SetStateAction<Dentist | null>>;
  availableDates: Date[];
  setAvailableDates: React.Dispatch<React.SetStateAction<Date[]>>;
  availableTimeslots: string[];
  setAvailableTimeslots: React.Dispatch<React.SetStateAction<string[]>>;
  selectedDate: Date | null;
  setSelectedDate: React.Dispatch<React.SetStateAction<Date | null>>;
  selectedTime: string | null;
  setSelectedTime: React.Dispatch<React.SetStateAction<string | null>>;
  purpose: string | null;
  setPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  othersPurpose: string | null;
  setOthersPurpose: React.Dispatch<React.SetStateAction<string | null>>;
  currentStep: string;
  setCurrentStep: React.Dispatch<React.SetStateAction<string>>;
  loadAvailableDates: () => Promise<void>;
  patient: Patient | null;
  patientId: string | null;
  firstName: string | null;
  lastName: string | null;
  mobileNumber: string | null;
  gender: Gender | null;
  birthday: string | null;
  setFirstName: React.Dispatch<React.SetStateAction<string | null>>;
  setLastName: React.Dispatch<React.SetStateAction<string | null>>;
  setMobileNumber: React.Dispatch<React.SetStateAction<string | null>>;
  setGender: React.Dispatch<React.SetStateAction<Gender | null>>;
  setBirthday: React.Dispatch<React.SetStateAction<string | null>>;
  durationInMinutes: number;
  services: Service[];
  sendOtp: () => Promise<void>;
  resendOtp: () => Promise<void>;
  verifyOtp: (otp: string) => Promise<void>;
  request: Request | null;
  confirmRequest: () => Promise<void>;
  reset: () => void;
  authenticatedPatientId: string | null;
}

const ScheduleFlowContext = createContext<ScheduleFlowContextProps | undefined>(
  undefined
);

interface ScheduleFlowProviderProps {
  children: React.ReactNode;
}

export const ScheduleFlowProvider: React.FC<ScheduleFlowProviderProps> = ({
  children
}) => {
  const posthog = usePostHog();
  const displayToast = useToast();
  const { setLanguageFromClinic } = useLanguage();
  const { id: clinicId } = useParams<{ id: string }>();

  const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
  const [availableDates, setAvailableDates] = useState<Date[]>([]);
  const [availableTimeslots, setAvailableTimeslots] = useState<string[]>([]);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<string | null>(null);
  const [purpose, setPurpose] = useState<string | null>(null);
  const [othersPurpose, setOthersPurpose] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<string>(
    SCHEDULE_FLOW_STEPS.SELECT_DENTIST
  );
  const [maxStepReached, setMaxStepReached] = useState<string>(
    SCHEDULE_FLOW_STEPS.SELECT_DENTIST
  );
  const [patientId, setPatientId] = useState<string | null>(null);
  const [firstName, setFirstName] = useState<string | null>(null);
  const [lastName, setLastName] = useState<string | null>(null);
  const [mobileNumber, setMobileNumber] = useState<string | null>(null);
  const [gender, setGender] = useState<Gender | null>(null);
  const [birthday, setBirthday] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState<boolean>(false);
  const [authenticatedPatientId, setAuthenticatedPatientId] = useState<
    string | null
  >(null);
  const [authenticatedMobileNumber, setAuthenticatedMobileNumber] = useState<
    string | null
  >(null);

  const createRequestMutation = useMutation({
    mutationFn: createRequest,
    retry: false,
    onSuccess: () => {
      setCurrentStep(SCHEDULE_FLOW_STEPS.THANK_YOU);
    },
    onError: (error) => {
      displayToast({
        message: `Error creating request: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const createPatientMutation = useMutation({
    mutationFn: createPatient,
    retry: false,
    onError: (error) => {
      displayToast({
        message: `Error creating patient: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const updatePatientMutation = useMutation({
    mutationFn: updatePatient,
    retry: false,
    onError: (error) => {
      displayToast({
        message: `Error updating patient: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const sendOtpMutation = useMutation({
    mutationFn: async (payload: { phoneNumber: string }) => {
      if (!selectedDentist?.patient_verification_enabled) {
        if (!patientId) {
          const uid = generateUid();
          await createPatientMutation.mutateAsync({
            ...patient,
            id: uid,
            user_id: authenticatedPatientId
          });
          setPatientId(uid);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
        return;
      }

      if (
        payload.phoneNumber &&
        authenticated &&
        `63${payload.phoneNumber}` === authenticatedMobileNumber &&
        authenticatedPatientId
      ) {
        const existingPatient = await getPatient(authenticatedPatientId);
        if (existingPatient) {
          await updatePatientMutation.mutateAsync({
            patientId: authenticatedPatientId,
            patient
          });
        } else {
          await createPatientMutation.mutateAsync({
            ...patient,
            id: authenticatedPatientId,
            is_user: true,
            user_id: authenticatedPatientId
          });
        }

        if (!patientId) {
          const uid = generateUid();
          await createPatientMutation.mutateAsync({
            ...patient,
            id: uid,
            user_id: authenticatedPatientId
          });
          setPatientId(uid);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
      } else {
        const { error } = await supabase.functions.invoke('otp', {
          body: {
            operation: 'send',
            payload: {
              phone_number: `63${payload.phoneNumber}`
            }
          }
        });

        if (error) {
          throw new Error(`Error sending OTP: ${error.message}`);
        } else {
          setCurrentStep(SCHEDULE_FLOW_STEPS.VERIFICATION);
        }
      }
    },
    retry: false,
    onError: () => {
      displayToast({
        message: `Failed to send OTP. Please try again or contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const resendOtpMutation = useMutation({
    mutationFn: async (phoneNumber: string) => {
      const { error } = await supabase.functions.invoke('otp', {
        body: {
          operation: 'resend',
          payload: {
            phone_number: `63${phoneNumber}`
          }
        }
      });

      if (error) {
        throw new Error(`Error resending OTP: ${error.message}`);
      }
    },
    retry: false,
    onError: () => {
      displayToast({
        message: `Failed to resend OTP. Please try again or contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const verifyOtpMutation = useMutation({
    mutationFn: async (payload: { phoneNumber: string; otp: string }) => {
      const {
        data: { session },
        error
      } = await supabase.auth.verifyOtp({
        phone: `63${payload.phoneNumber}`,
        token: payload.otp,
        type: 'sms'
      });

      if (error) {
        throw new Error(`Error verifying OTP: ${error.message}`);
      }

      if (session && session.user.id && session.user.phone) {
        const existingPatient = await getPatient(session.user.id);
        if (existingPatient) {
          await updatePatientMutation.mutateAsync({
            patientId: session.user.id,
            patient
          });
        } else {
          await createPatientMutation.mutateAsync({
            ...patient,
            id: session.user.id,
            is_user: true,
            user_id: session.user.id
          });
        }

        if (!patientId) {
          const uid = generateUid();
          await createPatientMutation.mutateAsync({
            ...patient,
            id: uid,
            user_id: session.user.id
          });
          setPatientId(uid);
        }
        setCurrentStep(SCHEDULE_FLOW_STEPS.CONFIRMATION);
      }
    },
    retry: false,
    onError: (error) => {
      displayToast({
        message: `${error instanceof Error ? error.message : 'Unknown error.'}`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const loadAvailableDatesMutation = useMutation({
    mutationFn: async (
      payload: { forceRefresh: boolean } = { forceRefresh: false }
    ) => {
      if (!selectedDentist) {
        displayToast({
          message: `No dentist selected. Please select a dentist and try again.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
        return;
      }

      if (!dentistHours || !requests) {
        displayToast({
          message: `Loading dentist schedule, please wait and try again...`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
        return;
      }

      if (availableDates.length > 0 && !payload.forceRefresh) {
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
        return;
      }

      const today = moment();
      const maximumDate = moment()
        .add(MAXIMUM_DATE_RANGE_IN_MONTHS, 'months')
        .endOf('week');
      const dates: Date[] = [];

      const date = today.clone();
      while (date.isBefore(maximumDate)) {
        const timeslots = getTimeslotsForDate(date.toDate());
        if (timeslots.length > 0) {
          dates.push(date.toDate());
        }
        date.add(1, 'day');
      }

      return dates;
    },
    onSuccess: (dates?: Date[]) => {
      if (!dates) {
        return;
      }

      if (dates.length > 0) {
        setAvailableDates(dates);
        setSelectedDate(dates[0]);
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
      } else {
        displayToast({
          message: `${formatDentistName({
            dentist: selectedDentist!,
            returnDentistFullName: true
          })} is unavailable. ${
            (dentists ?? []).length > 1
              ? 'Please choose another dentist or contact us at '
              : 'For assistance, please contact us at '
          }${formatMobileNumber(clinic?.mobile_number)}.`,
          duration: 8000,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
      }
    },
    onError: (error) => {
      displayToast({
        message: `Error fetching dentist schedule: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const confirmRequestMutation = useMutation({
    mutationFn: async () => {
      if (!selectedDentist?.id) {
        throw new Error('No dentist selected');
      }

      const existingRequests = await getDentistFutureAppointmentsAndRequests(
        selectedDentist.id!
      );
      const conflictingRequests = existingRequests.filter((existingRequest) => {
        const requestStartTime = moment(
          `${request.date} ${request.start_time}`,
          'YYYY-MM-DD HH:mm'
        );
        const requestEndTime = moment(
          `${request.date} ${request.end_time}`,
          'YYYY-MM-DD HH:mm'
        );
        const existingStartTime = moment(
          `${existingRequest.date} ${existingRequest.start_time}`,
          'YYYY-MM-DD HH:mm'
        );
        const existingEndTime = moment(
          `${existingRequest.date} ${existingRequest.end_time}`,
          'YYYY-MM-DD HH:mm'
        );

        return (
          requestStartTime.isBetween(
            existingStartTime,
            existingEndTime,
            null,
            '[)'
          ) ||
          requestEndTime.isBetween(
            existingStartTime,
            existingEndTime,
            null,
            '(]'
          ) ||
          existingStartTime.isBetween(
            requestStartTime,
            requestEndTime,
            null,
            '[)'
          ) ||
          existingEndTime.isBetween(
            requestStartTime,
            requestEndTime,
            null,
            '(]'
          )
        );
      });

      if (conflictingRequests.length > 0) {
        displayToast({
          message:
            'The timeslot you selected has just been booked by another patient. Please choose a different timeslot.',
          duration: 8000,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        await loadAvailableDatesMutation.mutateAsync({ forceRefresh: true });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DATE);
        return;
      }

      return await createRequestMutation.mutateAsync(request);
    },
    retry: false,
    onSuccess: () => {
      setCurrentStep(SCHEDULE_FLOW_STEPS.THANK_YOU);
    },
    onError: (error) => {
      displayToast({
        message: `Error confirming request: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,

        position: 'bottom',
        positionAnchor: 'nextButton'
      });
    }
  });

  const {
    data: clinic,
    isLoading: loadingClinic,
    isFetched: loadedClinic
  }: UseQueryResult<Clinic, Error> = useQuery({
    queryKey: ['clinic', clinicId],
    queryFn: async () => {
      if (!clinicId || !isValidUid(clinicId)) {
        displayToast({
          message:
            'Invalid clinic ID. Please double check the URL and try again.',
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        return null;
      }
      return getClinic(clinicId);
    },
    enabled: !!clinicId && isValidUid(clinicId)
  });

  const {
    data: dentists,
    isLoading: loadingDentists,
    isFetched: loadedDentists
  }: UseQueryResult<Dentist[], Error> = useQuery({
    queryKey: ['dentists', clinicId],
    queryFn: async () => {
      if (!clinicId || !isValidUid(clinicId)) {
        return [] as Dentist[];
      }
      try {
        const dentistsData = await getDentistsByClinicId(clinicId);
        return [...dentistsData].sort((a, b) =>
          a.first_name.localeCompare(b.first_name)
        );
      } catch (error) {
        displayToast({
          message: `Error fetching dentists: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        return [] as Dentist[];
      }
    },
    enabled: !!clinicId && isValidUid(clinicId)
  });

  const {
    data: dentistHours,
    isLoading: loadingDentistHours,
    isFetched: loadedDentistHours
  }: UseQueryResult<DentistHour[], Error> = useQuery({
    queryKey: ['dentistHours', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [];
      try {
        const result = await getDentistHoursByDentistId(selectedDentist.id);
        if (result.length > 0) {
          const hours: { [key: number]: DentistHour | null } = {};
          result.forEach((dentistHour) => {
            hours[dentistHour.day] = dentistHour.open ? dentistHour : null;
          });
          return result;
        } else {
          return [];
        }
      } catch (error) {
        displayToast({
          message: `Error fetching dentist hours: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_PURPOSE);
        return [];
      }
    },
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const {
    data: requests,
    isLoading: loadingRequests,
    isFetched: loadedRequests
  }: UseQueryResult<Request[], Error> = useQuery({
    queryKey: ['dentistRequests', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [];
      try {
        const result = await getDentistFutureAppointmentsAndRequests(
          selectedDentist.id
        );
        return result;
      } catch (error) {
        displayToast({
          message: `Error fetching dentist appointments: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
        return [];
      }
    },
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const {
    data: services,
    isLoading: loadingServices,
    isFetched: loadedServices
  }: UseQueryResult<Service[], Error> = useQuery({
    queryKey: ['services', selectedDentist?.id],
    queryFn: async () => {
      if (!selectedDentist?.id) return [] as Service[];
      try {
        const options = await getServicesByDentistId(selectedDentist.id);
        if (options.length > 0) {
          return options;
        } else {
          const defaultOptions = await getDefaultServices();
          return defaultOptions;
        }
      } catch (error) {
        displayToast({
          message: `Error fetching purpose options: ${error instanceof Error ? error.message : 'Unknown error'}. Please contact support.`,
          position: 'bottom',
          positionAnchor: 'nextButton'
        });
        setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
        return [] as Service[];
      }
    },
    enabled: !!selectedDentist?.id && isValidUid(selectedDentist.id)
  });

  const durationInMinutes: number = useMemo(() => {
    if (purpose && services && services.length > 0) {
      const calculatedDuration = purpose
        .split(PURPOSE_JOIN_SEPARATOR)
        .sort((a, b) => {
          const durationA =
            services.find((option) => option.name === a)?.duration ||
            DEFAULT_DURATION_IN_MINUTES;
          const durationB =
            services.find((option) => option.name === b)?.duration ||
            DEFAULT_DURATION_IN_MINUTES;
          return durationB - durationA;
        })
        .reduce(
          (total, purpose, index, purposes) =>
            total +
            (services.find((option) => option.name === purpose)?.duration ||
              DEFAULT_DURATION_IN_MINUTES) -
            (purposes.length > 1 && index !== 0
              ? OVERLAP_TIME_REDUCTION_IN_MINUTES
              : 0),
          0
        );

      return Math.min(calculatedDuration, MAXIMUM_DURATION_IN_MINUTES);
    }

    return DEFAULT_DURATION_IN_MINUTES;
  }, [services, purpose]);

  const patient: Patient = useMemo(
    () => ({
      first_name: formatName(firstName),
      last_name: formatName(lastName),
      mobile_number: mobileNumber,
      gender,
      birthday
    }),
    [firstName, lastName, mobileNumber, gender, birthday]
  );

  const request: Request = useMemo(
    () => ({
      date: selectedDate ? moment(selectedDate).format('YYYY-MM-DD') : null,
      start_time: selectedTime,
      end_time: selectedTime
        ? moment(selectedTime, 'HH:mm')
            .add(durationInMinutes, 'minutes')
            .format('HH:mm')
        : null,
      dentist_id: selectedDentist?.id!,
      status: REQUEST_STATUS_OPTIONS.PENDING,
      purpose:
        purpose === SELECT_MODAL_OTHERS &&
        othersPurpose &&
        othersPurpose.trim() !== ''
          ? `${purpose}: ${othersPurpose}`
          : purpose,
      patient_id: patientId
    }),
    [
      selectedDate,
      selectedTime,
      durationInMinutes,
      selectedDentist,
      purpose,
      othersPurpose,
      patientId
    ]
  );

  // Set language based on clinic data
  useEffect(() => {
    if (clinic && clinic.language) {
      setLanguageFromClinic(clinic.language);
    }
  }, [clinic, setLanguageFromClinic]);

  const loading =
    loadingClinic ||
    !loadedClinic ||
    loadingDentists ||
    !loadedDentists ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingDentistHours || !loadedDentistHours)) ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingRequests || !loadedRequests)) ||
    (currentStep === SCHEDULE_FLOW_STEPS.SELECT_PURPOSE &&
      (loadingServices || !loadedServices)) ||
    createRequestMutation.isPending ||
    createPatientMutation.isPending ||
    updatePatientMutation.isPending ||
    sendOtpMutation.isPending ||
    resendOtpMutation.isPending ||
    verifyOtpMutation.isPending ||
    loadAvailableDatesMutation.isPending ||
    confirmRequestMutation.isPending;

  const getTimeslotsForDate = useCallback(
    (date: Date) => {
      if (!selectedDentist || !dentistHours || !requests) {
        return [];
      }

      const dayOfWeek = moment(date).day();
      const dentistDayHours = dentistHours.find(
        (hour) => hour.day === dayOfWeek && hour.open
      );

      if (!dentistDayHours) {
        return [];
      }

      const startTime = moment(
        `${moment(date).format('YYYY-MM-DD')} ${dentistDayHours.start_time}`,
        'YYYY-MM-DD HH:mm'
      );
      const endTime = moment(
        `${moment(date).format('YYYY-MM-DD')} ${dentistDayHours.end_time}`,
        'YYYY-MM-DD HH:mm'
      );
      const timeslots: string[] = [];

      let currentTime = startTime.clone();
      const bufferTime = moment().add(TIMESLOT_BUFFER_IN_HOURS, 'hours');

      const isSlotBlocked = (
        slotStartTime: moment.Moment,
        slotEndTime: moment.Moment
      ) => {
        return requests
          .filter((request) =>
            moment(date).isSame(moment(request.date, 'YYYY-MM-DD'), 'day')
          )
          .find((request) => {
            const requestStartTime = moment(
              `${request.date} ${request.start_time}`,
              'YYYY-MM-DD HH:mm'
            );
            const requestEndTime = moment(
              `${request.date} ${request.end_time}`,
              'YYYY-MM-DD HH:mm'
            );

            return (
              slotStartTime.isBetween(
                requestStartTime,
                requestEndTime,
                null,
                '[)'
              ) ||
              slotEndTime.isBetween(
                requestStartTime,
                requestEndTime,
                null,
                '(]'
              ) ||
              requestStartTime.isBetween(
                slotStartTime,
                slotEndTime,
                null,
                '[)'
              ) ||
              requestEndTime.isBetween(slotStartTime, slotEndTime, null, '(]')
            );
          });
      };

      const duration = durationInMinutes || DEFAULT_DURATION_IN_MINUTES;

      while (currentTime.isBefore(endTime)) {
        const slotEndTime = currentTime.clone().add(duration, 'minutes');

        const conflictingRequest = isSlotBlocked(currentTime, slotEndTime);

        if (
          !conflictingRequest &&
          currentTime.isAfter(bufferTime) &&
          slotEndTime.isSameOrBefore(endTime)
        ) {
          timeslots.push(currentTime.format('HH:mm'));
        } else {
          if (conflictingRequest) {
            currentTime = moment(
              `${conflictingRequest.date} ${conflictingRequest.end_time}`,
              'YYYY-MM-DD HH:mm'
            );
            continue;
          }
        }

        const remainder =
          DEFAULT_DURATION_IN_MINUTES -
          (currentTime.minute() % DEFAULT_DURATION_IN_MINUTES);

        currentTime.add(remainder, 'minute');
      }

      return timeslots;
    },
    [selectedDentist, dentistHours, requests, durationInMinutes]
  );

  const loadAvailableDates = async () => {
    await loadAvailableDatesMutation.mutateAsync({ forceRefresh: false });
  };

  const sendOtp = async () => {
    await sendOtpMutation.mutateAsync({ phoneNumber: mobileNumber! });
  };

  const resendOtp = async () => {
    await resendOtpMutation.mutateAsync(mobileNumber!);
  };

  const verifyOtp = async (otp: string) => {
    await verifyOtpMutation
      .mutateAsync({ phoneNumber: mobileNumber!, otp })
      .catch(() => {}); // Prevents sending error to Sentry and PostHog
  };

  const confirmRequest = async () => {
    await confirmRequestMutation.mutateAsync();
  };

  const reset = useCallback(async () => {
    await supabase.auth.signOut();
    setCurrentStep(SCHEDULE_FLOW_STEPS.SELECT_DENTIST);
    setSelectedDentist(null);
    setAvailableDates([]);
    setAvailableTimeslots([]);
    setPatientId(null);
    setFirstName(null);
    setLastName(null);
    setMobileNumber(null);
    setGender(null);
    setBirthday(null);
    setAuthenticatedMobileNumber(null);
    setAuthenticatedPatientId(null);
    setAuthenticated(false);
    setSelectedDate(null);
    setPurpose(null);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      const {
        data: { user }
      } = await supabase.auth.getUser();

      if (user) {
        const patient = await getPatient(user.id);
        setAuthenticated(true);
        setAuthenticatedMobileNumber(user.phone!);
        setAuthenticatedPatientId(user.id);
        if (patient) {
          setFirstName(patient.first_name);
          setLastName(patient.last_name);
          setMobileNumber(patient.mobile_number);
          setGender(patient.gender);
          setBirthday(patient.birthday);
        }
      }
    };

    fetchUser();
  }, []);

  useEffect(() => {
    if (selectedDate) {
      const timeslots = getTimeslotsForDate(selectedDate);
      setAvailableTimeslots(timeslots);
      setSelectedTime(timeslots[0]);
    } else {
      setAvailableTimeslots([]);
    }
  }, [selectedDate, getTimeslotsForDate]);

  useEffect(() => {
    if (currentStep === maxStepReached) {
      return;
    }

    if (
      Object.values(SCHEDULE_FLOW_STEPS).indexOf(currentStep) >
      Object.values(SCHEDULE_FLOW_STEPS).indexOf(maxStepReached)
    ) {
      setMaxStepReached(currentStep);
    }
  }, [currentStep, maxStepReached]);

  useEffect(() => {
    if (posthog && maxStepReached) {
      posthog.capture('step', {
        step: maxStepReached
      });
    }
  }, [maxStepReached, posthog]);

  useEffect(() => {
    /**
     * Sets the first available dentist as the default selection
     * if no dentist is currently selected and dentists are available
     */
    if (dentists && dentists.length > 0 && !selectedDentist) {
      setSelectedDentist(dentists[0]);
    }
  }, [dentists]);

  return (
    <ScheduleFlowContext.Provider
      value={{
        loading,
        clinic: clinic ?? null,
        dentists: dentists ?? [],
        selectedDentist,
        setSelectedDentist,
        availableDates,
        setAvailableDates,
        availableTimeslots,
        setAvailableTimeslots,
        selectedDate,
        setSelectedDate,
        selectedTime,
        setSelectedTime,
        purpose,
        setPurpose,
        othersPurpose,
        setOthersPurpose,
        currentStep,
        setCurrentStep,
        loadAvailableDates,
        patient,
        patientId,
        firstName,
        lastName,
        mobileNumber,
        gender,
        birthday,
        setFirstName,
        setLastName,
        setMobileNumber,
        setGender,
        setBirthday,
        durationInMinutes,
        services: services ?? [],
        sendOtp,
        resendOtp,
        verifyOtp,
        request,
        confirmRequest,
        reset,
        authenticatedPatientId
      }}
    >
      {children}
    </ScheduleFlowContext.Provider>
  );
};

export const useScheduleFlow = () => {
  const context = useContext(ScheduleFlowContext);
  if (context === undefined) {
    throw new Error(
      'useScheduleFlow must be used within a ScheduleFlowProvider'
    );
  }
  return context;
};
